import React, { useEffect, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router-dom";

import MainButton from "../../../../components/main-button";
import InputBox from "../../../../components/input-box/input-box";
import PersonalBasicData from "../components/personal-basic-data/personal-basic-data";
import RequiredDocumentsStep from "../new-insured/required-documents/required-documents-step";
import StepsMenu from "../../../../components/steps-menu/steps-menu";
import LeftInfoModal from "../../../../components/left-info-modal";
import BreadcrumbText from "../../../../components/components-text/breadcrumb-text";
import { images } from "../../../../assets";
import { TakerDataContainer } from "../../../hirings/new-hiring/health/health-taker/taker-data-style";
import { NewInsuredContainer } from "../new-insured/newInsuredStepper-style";

import useForm from "../hooks/useForm";
import { SupplementsContext } from "../../../../context/supplements/SupplementsContext";
import { SUPPLEMENTS_MATCH_PARAMS } from "../../../../constants/supplements";
import {
  MODAL_ASK_CONFIRMATION,
  MODAL_CHANGE_DATA_INFO,
  MODAL_CHANGE_DEFERRED,
  MODAL_CHANGED_OK,
  MODAL_CHIP_ERROR,
  MODAL_SEND_EMAIL_ERROR,
  MODAL_SEND_EMAIL_OK,
  MODAL_SUBMIT_DOCS_ERROR
} from "./modal-helpers";

import SupplementsServices from "../../../../services/SupplementsServices";
import { deepGet } from "../../../../utils/utility";
import {
  buttonList,
  initialData,
  isValidStep,
  stepsMenuProps,
  uploadDocument,
  validateBasicData,
} from "./utils";
import { getPolicyNumber } from "../utils";
import { transformPayType } from "../components/variable-data/utils";
import { validateEmailByHocelot } from "../new-insured/utils";
import { getMediatorCompleteName } from "../../../supplements/utils";
import { MatchParams, Taker } from "./basic-data.types";
import { LeftInfoModalProps } from "../../../../components/left-info-modal/left-info-modal-layout";

const BasicDataForm: React.FC<RouteComponentProps<MatchParams>> = ({ match, history }) => {
  const [step, setStep] = useState<number>(1);
  const [emailError, setEmailError] = useState<string>("");
  const [isAnyCheckTrue, setIsAnyCheckTrue] = useState<boolean>(false);
  const [isFirstTime, setIsFirstTime] = useState<boolean>(true);
  const [modal, setModal] = useState<Partial<LeftInfoModalProps | null>>(null);

  const supplementsContext = React.useContext(SupplementsContext);
  
  const formTitle = match.params.formTitle.replace(/-/g, ' ');
  const isClientChanges = match.params.formTitle === SUPPLEMENTS_MATCH_PARAMS.CAMBIO_DATOS_BASICOS;
  
  const { values, errors, handleChange, setErrors, handleMultipleChanges } = useForm(
    initialData(formTitle),
    () => validateBasicData(values, supplementsContext.supplementDocs),
    isFirstTime
  );
  const isMascota = values.type === "MASCOTA";

  useEffect(() => {
    setIsAnyCheckTrue(Object.values(values.check).some(value => value === true));
  }, [values.check]);

  useEffect(() => {
    if (!supplementsContext.supplements || supplementsContext.supplements.length === 0) {
      history.push(`/suplementos/${getPolicyNumber()}`);
    }
  }, []);

  const goToNextStep = async () => {
    const { errors, hasValidation } = validateBasicData(values, supplementsContext.supplementDocs);
    const errorEmail = await validateEmailByHocelot(deepGet(values, 'email') || '')
    if (values.check.email) {
      setEmailError(errorEmail);
    }
    if (isValidStep(hasValidation) && (!values.check.email || !errorEmail)) {
      setErrors({});
      setIsFirstTime(true);
      if (isClientChanges && !isMascota) {
        const modalContent = {
          ...MODAL_CHANGE_DATA_INFO,
          onClickAccept: () => setStep((prevValue: number) => prevValue + 1),
          close: () => setModal(null),
          secondaryAction: () => history.push(`/suplementos/${getPolicyNumber()}`),
        }
        setModal(modalContent);
      } else {
        setStep((prevValue: number) => prevValue + 1);
      }
    } else {
      setIsFirstTime(false);
      setErrors(errors);
    }
  };

  const goToPreviousStep = () => {
    if (step === 1) {
      history.push(`/suplementos/${getPolicyNumber()}`);
      return;
    }
    setErrors({});
    setStep((prevValue: number) => prevValue - 1);
  };

  const submitByEmail = async (tryDirectChange: boolean = false) => {
    const response = await SupplementsServices.sendBasicDataModificationsEmail({
      ...values,
      newPaymentType: transformPayType(values.newPaymentType),
    }, supplementsContext.sendTo, supplementsContext.isInsuredInSendaPolicy);

    if (response.data.status) {
      if (tryDirectChange) {
        const deferred = response.data.deferred;
        const modalContent = {
          ...(deferred ? { ...MODAL_CHANGE_DEFERRED} : { ...MODAL_CHANGED_OK }),
          onClickAccept: () => history.push(`/suplementos/${getPolicyNumber()}`),
          close: () => setModal(null),
        };
        setModal(modalContent);
      } else {
        const modalContent = {
          ...MODAL_SEND_EMAIL_OK,
          onClickAccept: () => history.push(`/suplementos/${getPolicyNumber()}`),
          close: () => setModal(null),
        }
        setModal(modalContent);
      }
    } else {
      const errorCode = response.data.errorCode;
      if (errorCode && errorCode === "KOMICROCHIP") {
        const modalContent = {
          ...MODAL_CHIP_ERROR,
          onClickAccept: goToPreviousStep,
          close: () => setModal(null),
        };
        setModal(modalContent);
        return;
      }

      const modalContent = {
        ...MODAL_SEND_EMAIL_ERROR,
        mainText: response.data.msg,
        onClickAccept: () => setModal(null),
        close: () => setModal(null),
      }
      setModal(modalContent);
    }
  };

  const validateForm = () => {
    const { errors, hasValidation } = validateBasicData(values, supplementsContext.supplementDocs);
    if (hasValidation.documents) {
      const modalContent = {
        ...MODAL_ASK_CONFIRMATION,
        mainText: `Se va a enviar una solicitud de ${formTitle.toLowerCase()}`,
        onClickAccept: isClientChanges ? uploadDocuments : submitByEmail,
        close: () => setModal(null),
      }
      setModal(modalContent);
    } else {
      setErrors(errors);
    }
  };

  const uploadDocuments = async () => {
    const docsToUpload = (values.documents && values.documents.uploadDocuments) || [];
    const takerData = supplementsContext.taker as Taker;
    const responseUploadDocs = await Promise.all(
      Array.from(docsToUpload).map((doc) => uploadDocument(doc, values, takerData)),
    );
    if (responseUploadDocs.some((res) => res === null)) {
      const modalContent: Partial<LeftInfoModalProps> = {
        ...MODAL_SUBMIT_DOCS_ERROR,
        onClickAccept: () => setModal(null),
        close: () => setModal(null),
      };
      setModal(modalContent);
    } else {
      const tryDirectChange = true;
      submitByEmail(tryDirectChange);
    }
  };

  const getIsNextButtonDisabled = () => {
    if (!isAnyCheckTrue) return true;
    if (isClientChanges && !values.checkPersonId) return true;
    return false;
  };

  return (
    <NewInsuredContainer>
      <div className="subTitle">
        <BreadcrumbText>
          {"NUEVO SUPLEMENTO - "}{formTitle}
        </BreadcrumbText>
      </div>
      <div className="personal-data-row form-width container-background">
        <div className="personal-data-item col-50">
          <div className="inputBox">
            <InputBox
              placeholder="Mediador"
              required={true}
              disabled={true}
              value={getMediatorCompleteName()}
              onChange={() => {}}
              type={"letters"}
            />
          </div>
        </div>
        <div className="personal-data-item col-50">
          <div className="inputBox">
            <InputBox
              placeholder="Datos de Póliza a modificar"
              required={true}
              disabled={true}
              value={values.policy}
              onChange={() => {}}
              type={"letters"}
            />
          </div>
        </div>
      </div>
      <div className="form-width">
        {step === 1 && (
          <PersonalBasicData
            formData={{ values, errors, handleChange }}
            step={step}
            handleMultipleChanges={handleMultipleChanges}
            formTitle={formTitle}
            emailError={emailError}
            setEmailError={setEmailError}
            isClientChanges={isClientChanges}
          />
        )}
        {step === 2 && (
          <RequiredDocumentsStep
            values={values}
            errors={errors}
            handleChange={handleChange}
            hideTitle
            hideInsurance
            setErrors={setErrors}
            docNum={isClientChanges ? values.docNumber : undefined}
          />
        )}
        <TakerDataContainer>
          <div className="colSin-100 topMargin20 bottomMargin20">
            <div className="personal-data-buttons">
              <div className="personal-data-buttons__item">
                <MainButton
                  withTab={true}
                  icon={true}
                  type={"white"}
                  iconImg={images.IconArrowLeftBlue}
                  disabled={false}
                  text="Atrás"
                  onClick={goToPreviousStep}
                />
              </div>
              <div className="personal-data-buttons__item">
                {step === 1 ? (
                  <MainButton
                    withTab={true}
                    text="Siguiente"
                    disabled={getIsNextButtonDisabled()}
                    onClick={goToNextStep}
                  />
                ) : (
                  <MainButton
                    withTab={true}
                    text="Enviar"
                    onClick={validateForm}
                  />
                )}
              </div>
            </div>
          </div>
          <StepsMenu
            policyData={stepsMenuProps.policyData}
            buttonFunction={setStep}
            actualStep={step}
            buttonList={buttonList(formTitle)}
          />

          {modal && (
            <LeftInfoModal
              className={modal.className || ""}
              active={modal.active}
              img={modal.img}
              mainTitle={modal.mainTitle}
              mainText={modal.mainText}
              secondaryText={modal.secondaryText || ""}
              buttonText={modal.buttonText}
              closeButtonText={modal.closeButtonText || ""}
              close={() => setModal(null)}
              onClickAccept={modal.onClickAccept}
              noSecondaryButton={modal.noSecondaryButton}
              secondaryAction={modal.secondaryAction}
            />
          )}
        </TakerDataContainer>
      </div>
    </NewInsuredContainer>
  );
};

export default withRouter(BasicDataForm);
